import { MinMaxSliderComponent } from './components/forms/min-max-slider/min-max-slider.component';
import { GlobalStyleLoaderComponent } from './global-style-loader/global-style-loader.component';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, NO_ERRORS_SCHEMA, Injector, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';
import { routes } from './routes';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { RouterModule } from '@angular/router';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { CacheModule } from '@ngx-cache/core';

import { HeaderComponent } from './components/header/header.component';
import { GoogleMapComponent } from './components/google-map/google-map.component';
import { ListingsService } from './services/listings.service';
import { AgentService } from './services/agent.service';
import { CommunicationService } from './services/communication.service';
import { ApiService } from './services/api.service';
import { CredentialsService } from './services/credentials.service';
import { ErrorService } from './services/error.service';
import { LoginService } from './services/login.service';
import { PreferencesService } from './services/preferences.service';
import { CacheService } from './services/cache.service';
import { RoutingHistoryService } from './services/routing-history.service';

import { CardPreviewComponent } from './components/card-preview/card-preview.component';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { SearchResultsComponent } from './components/search-bar/search-results/search-results.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { SimpleLoginComponent } from './simple-login/simple-login.component';
import { DrawerComponent } from './components/drawer/drawer.component';
import { ListingDetailsComponent } from './components/listing-details/listing-details.component';
import { HomePageComponent } from './components/home-page/home-page.component';
import { GalleryViewComponent, ListingGalleryViewComponent } from './components/gallery-view/gallery-view.component';
import { PillComponent } from './components/pills/pill.component';
import { PillsListComponent } from './components/pills/pills-list/pills-list.component';

import { MyRemaxComponent } from './components/my-remax/my-remax.component';
import { FavouriteListingsComponent } from './components/my-remax/favourite-listings/favourite-listings.component';
import { SavedSearchesComponent } from './components/my-remax/saved-searches/saved-searches.component';
import { CheckboxComponent } from './components/forms/checkbox/checkbox.component';
import { SwitchComponent } from './components/forms/switch/switch.component';
import { ContactAgentComponent } from './components/contact-agent/contact-agent.component';
import { DrawerPageComponent } from './components/drawer/drawer-page/drawer-page.component';
import { RadioGroupComponent } from './components/forms/radio-group/radio-group.component';
import { PanelMessageComponent } from './components/panel-message/panel-message.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SignInComponent } from './components/login/sign-in/sign-in.component';
import { SelectComponent } from './components/forms/select/select.component';
import { AgentProfileComponent } from './components/page-profile/agent-profile/agent-profile.component';
import { SaveSearchComponent } from './components/save-search/save-search.component';
import { DetailSectionComponent } from './components/listing-details/detail-section/detail-section.component';
import { ForgotPasswordComponent } from './components/login/forgot-password/forgot-password.component';
import { LoginComponent } from './components/login/login.component';
import { ResetPasswordComponent } from './components/login/reset-password/reset-password.component';
import { OfficeProfileComponent } from './components/page-profile/office-profile/office-profile.component';
import { ProfileHeaderComponent } from './components/page-profile/profile-header/profile-header.component';
import { SavedSearchComponent } from './components/my-remax/saved-searches/saved-search/saved-search.component';
import { GoogleMapsService } from './services/google-maps.service';
import { MyAccountComponent } from './components/my-account/my-account.component';
import { MortgageCalculatorComponent } from './components/mortgage-calculator/mortgage-calculator.component';
import { InputComponent } from './components/forms/input/input.component';
import { MortgageService } from './services/mortgage.service';
import { SocialComponent } from './components/login/social/social.component';
import { SocialService } from './services/social.service';
import { LoginReminderComponent } from './components/login/login-reminder/login-reminder.component';
import { LoaderComponent } from './components/loader/loader.component';
import { MyMissingTranslationHandler } from './handlers/my-missing-translation-handler';
import { SubNavComponent } from './components/home-page/sub-nav/sub-nav.component';
import { LeftNavComponent } from './components/side-nav/left-nav/left-nav.component';
import { RightNavComponent } from './components/side-nav/right-nav/right-nav.component';
import { FooterComponent } from './components/footer/footer.component';
import { AgentCardComponent } from './components/listing-details/agent-card/agent-card.component';
import { ShareBarComponent } from './components/share-bar/share-bar.component';
import { MlsResultComponent } from './components/search-bar/search-results/mls-result/mls-result.component';
import { RequestSentComponent } from './components/contact-agent/request-sent/request-sent.component';
import { ChangesSavedComponent } from './components/my-remax/saved-searches/changes-saved/changes-saved.component';
import { ListingSummaryComponent, ListingSummaryDesktopComponent, ListingSummaryMobileComponent } from './components/listing-details/listing-summary/listing-summary.component';
import { GalleryViewDesktopComponent } from './components/gallery-view/gallery-view-desktop/gallery-view-desktop.component';
import { SiteLinksComponent } from './components/site-links/site-links.component';
import { ModalComponent } from './components/modal/modal.component';
import { MyRemaxMenuComponent } from './components/header/my-remax-menu/my-remax-menu.component';
import { BreadCrumbsComponent } from './components/bread-crumbs/bread-crumbs.component';
import { ConnectSocialBarComponent } from './components/page-profile/connect-social-bar/connect-social-bar.component';
import { PageProfileComponent } from './components/page-profile/page-profile.component';
import { OfficeService } from './services/office.service';
import { AgentListComponent } from './components/agent-list/agent-list.component';
import {CurrencyPipe, TitleCasePipe} from '@angular/common';
import { AgentAwardListComponent } from './components/agent-award-list/agent-award-list.component';
import { environment } from '../environments/environment-wrapper';
import { SearchPageComponent } from './components/search-page/search-page.component';
import { OfficeCardComponent } from './components/office-card/office-card.component';
import { PaginationComponent } from './components/gallery-view/pagination/pagination.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { NguiMapModule } from '@ngui/map';
import { LogoComponent } from './components/logo/logo.component';
import { CardContactInfoComponent } from './components/card-contact-info/card-contact-info.component';
import { FiltersCommercialDesktopComponent } from './components/filters/filters-commercial/filters-commercial.desktop.component';
import { FiltersCommercialMobileComponent } from './components/filters/filters-commercial/filters-commercial.mobile.component';
import { FiltersDesktopComponent, FiltersMobileComponent } from './components/filters/filters.component';
import { FiltersSearchComponent } from './components/filters/filters-search/filters-search.component';
import { FiltersSearchMobileComponent } from './components/filters/filters-search/filters-search.mobile.component';
import { NotificationPopupModalComponent } from './components/notification-popup-modal/notification-popup-modal.component';
import { MoreToSeeComponent } from './components/listing-details/more-to-see/more-to-see.component';
import { PreRenderService } from './services/pre-render.service';
import { OfficeResolver } from './resolvers/office-resolver';
import { UserResolver } from './resolvers/user-resolver';
import { ScreenSizeResolver } from './resolvers/screen-size-resolver';
import { ListingResolver } from './resolvers/listing-resolver';
import { AgentResolver } from './resolvers/agent-resolver';
import { MapCityResolver } from './resolvers/map-city-resolver';
import { OffMarketListingResolver } from './resolvers/off-market-listing-resolver';
import { BusinessOpportunitiesResolver } from './resolvers/business-opportunities-resolver';
import { MetaTagService } from './services/meta-tag.service';
import { RenderPlaceholderComponent } from './components/render-placeholder/render-placeholder.component';
import { PropTypeResolver } from './resolvers/prop-type-resolver';
import { CommunicationSearchingResolver } from './resolvers/communication-searching-resolver';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { BlogPostsComponent } from './components/blog-posts/blog-posts.component';
import { CircleInitialsComponent } from './components/circle-initials/circle-initials.component';
import { IonRangeSliderModule } from 'ng2-ion-range-slider';
import { APP_DI_CONFIG, APP_CONFIG } from './app-config.constants';
import { LeadsService } from './services/leads.service';
import { QuebecModalComponent } from './components/quebec-modal/quebec-modal.component';
import { GalleryRelatedOfficeComponent } from './components/gallery-view/gallery-related-office/gallery-related-office.component';
import { RmTransferHttpCacheInterceptor } from './services/interceptor/rm-transfer-http-cache-interceptor';
import { ListingSummaryDesktopContentComponent } from './components/listing-details/listing-summary/listing-summary-desktop-content/listing-summary-desktop-content.component';
import { SplashIntroResolver } from './resolvers/splash-intro-resolver';
import { Angulartics2Module } from 'angulartics2';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { AgentProvinceCityResolver } from './resolvers/agent-province-city-resolver';
import { ProvinceResolver } from './resolvers/province-resolver';
import { LandingPageConfigResolver } from './resolvers/page-config-resolver';
import { CommercialListingResolver } from './resolvers/commercial-listings-resolver';
import { ListingQueryParamResolver } from './resolvers/listing-query-param-resolver';
import { SavedSearchUnsubscribeResolver } from './resolvers/saved-search-unsubscribe-resolver';
import { BlogService } from './services/blog.service';
import { HomeEstimatorDialogComponent } from './components/home-estimator/home-estimator-dialog/home-estimator-dialog.component';
import { HomeEstimatorComponent } from './components/home-estimator/home-estimator.component';
import { HomeEstimatorService } from './services/home-estimator.service';
import { HomeEstimatorEstimateComponent } from './components/home-estimator/home-estimator-estimate/home-estimator-estimate.component';
import { HomeEstimatesComponent } from './components/home-estimator/home-estimates.component';
import {DatePipe} from '@angular/common';
import { SetRouteCodeGuard } from './guards/set-route-code.guard';
import { RecaptchaComponent } from './components/recaptcha/recaptcha.component';
import { DemographicsService } from './services/demographics.service';
import {HomeEstimatorSuggestionsComponent} from './components/home-estimator/home-estimator-suggestions/home-estimator-suggestions.component';
import { LuxuryListingResolver } from './resolvers/luxury-listing-resolver';
import { OpenHouseComponent } from './components/listing-details/open-house/open-house.component';
import { RemaxRadarComponent } from './components/listing-details/remax-radar/remax-radar.component';
import { SharedModule } from './shared/shared.module';
import { DemographicsSectionComponent } from './components/listing-details/demographics-section/demographics-section.component';
import { ErrorComponent } from './components/error/error.component';
import { OpenHouseResolver } from './resolvers/open-house-resolver';
import { ToggleFavouriteAgentComponent } from './components/page-profile/toggle-favourite-agent/toggle-favourite-agent.component';
import { AgentReviewsComponent } from './components/page-profile/agent-profile/agent-reviews/agent-reviews.component';
import {PersonaTypeResolver} from './resolvers/persona-type.resolver';
import { MaxPerxSessionGuard } from './guards/maxperx-session.guard';
import {InternalLinksService} from './services/internalLinks.service';

// AoT requires an exported function for factories
export function HttpLoaderFactory(injector: Injector) {
  const http = injector.get(HttpClient);
  return new TranslateHttpLoader(http, environment.staticResourceRoot + 'assets/i18n/', '.ng_app_version.json');
}

@NgModule({
  declarations: [
    AppComponent,
    GoogleMapComponent,
    HeaderComponent,
    CardPreviewComponent,
    NotificationsComponent,
    SimpleLoginComponent,
    DrawerComponent,
    MinMaxSliderComponent,
    SearchBarComponent,
    SearchResultsComponent,
    ListingDetailsComponent,
    DemographicsSectionComponent,
    HomePageComponent,
    GalleryViewComponent,
    PillComponent,
    PillsListComponent,
    MyRemaxComponent,
    FavouriteListingsComponent,
    SavedSearchesComponent,
    CheckboxComponent,
    SwitchComponent,
    DrawerPageComponent,
    ContactAgentComponent,
    RadioGroupComponent,
    PanelMessageComponent,
    SideNavComponent,
    SignInComponent,
    SelectComponent,
    AgentProfileComponent,
    DetailSectionComponent,
    SaveSearchComponent,
    ForgotPasswordComponent,
    LoginComponent,
    ResetPasswordComponent,
    OfficeProfileComponent,
    ProfileHeaderComponent,
    SavedSearchComponent,
    MyAccountComponent,
    MortgageCalculatorComponent,
    InputComponent,
    SocialComponent,
    LoginReminderComponent,
    LoaderComponent,
    SubNavComponent,
    LeftNavComponent,
    RightNavComponent,
    FooterComponent,
    AgentCardComponent,
    ShareBarComponent,
    MlsResultComponent,
    RequestSentComponent,
    ChangesSavedComponent,
    ListingSummaryComponent,
    ListingSummaryDesktopComponent,
    ListingSummaryMobileComponent,
    FiltersMobileComponent,
    FiltersDesktopComponent,
    FiltersCommercialDesktopComponent,
    FiltersCommercialMobileComponent,
    GalleryViewDesktopComponent,
    SiteLinksComponent,
    ModalComponent,
    MyRemaxMenuComponent,
    BreadCrumbsComponent,
    ConnectSocialBarComponent,
    PageProfileComponent,
    AgentListComponent,
    AgentAwardListComponent,
    ListingGalleryViewComponent,
    SearchPageComponent,
    OfficeCardComponent,
    PaginationComponent,
    PageNotFoundComponent,
    MoreToSeeComponent,
    FiltersSearchComponent,
    FiltersSearchMobileComponent,
    MoreToSeeComponent,
    GalleryRelatedOfficeComponent,
    RenderPlaceholderComponent,
    LandingPageComponent,
    CircleInitialsComponent,
    GlobalStyleLoaderComponent,
    LandingPageComponent,
    BlogPostsComponent,
    QuebecModalComponent,
    ListingSummaryDesktopContentComponent,
    LogoComponent,
    HomeEstimatesComponent,
    HomeEstimatorComponent,
    HomeEstimatorDialogComponent,
    HomeEstimatorEstimateComponent,
    HomeEstimatorSuggestionsComponent,
    CardContactInfoComponent,
    RecaptchaComponent,
    OpenHouseComponent,
    RemaxRadarComponent,
    ErrorComponent,
    ToggleFavouriteAgentComponent,
    AgentReviewsComponent,
    NotificationPopupModalComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'remaxdotca' }),
    Angulartics2Module.forRoot([Angulartics2GoogleTagManager]),
    SharedModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(routes,
      {
        initialNavigation: 'enabled',
      }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [Injector],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler
      }
    }),
    HttpClientModule,
    NguiMapModule.forRoot({ apiUrl: 'https://maps.google.com/maps/api/js?key=AIzaSyAkk2vtPJgOdzRiaCiBoqCBf2DtGrM9pGU&libraries=places,drawing' }),
    FormsModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    IonRangeSliderModule,
    CacheModule.forRoot()
  ],
  exports: [
    MinMaxSliderComponent,
  ],
  providers: [
    PreferencesService,
    CacheService,
    DemographicsService,
    ListingsService,
    LeadsService,
    CommunicationService,
    BlogService,
    AgentService,
    ApiService,
    CredentialsService,
    LoginService,
    ErrorService,
    GoogleMapsService,
    MortgageService,
    SocialService,
    OfficeService,
    InternalLinksService,
    CurrencyPipe,
    TitleCasePipe,
    PreRenderService,
    RoutingHistoryService,
    MetaTagService,
    RmTransferHttpCacheInterceptor,
    HomeEstimatorService,
    DatePipe,
    SetRouteCodeGuard,
    MaxPerxSessionGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: RmTransferHttpCacheInterceptor,
      multi: true
    },
    // Resolvers
    OfficeResolver, UserResolver, ScreenSizeResolver, ListingResolver, AgentResolver, MapCityResolver, CommunicationSearchingResolver, PropTypeResolver,
    SplashIntroResolver, AgentProvinceCityResolver, CommercialListingResolver, ProvinceResolver, LandingPageConfigResolver, LuxuryListingResolver, OpenHouseResolver, PersonaTypeResolver,
    ListingQueryParamResolver, SavedSearchUnsubscribeResolver, OffMarketListingResolver, BusinessOpportunitiesResolver,
    {
      provide: APP_CONFIG,
      useValue: APP_DI_CONFIG
    }
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA] // we include this so that we can use multi select ng contents for components
})
export class AppModule { }
